import {
  DEFAULT_LANGUAGE_CODE,
  normalizeLanguageCode,
  normalizeUiLanguageCode,
} from "./languages.js";

const ONBOARDING_COMPLETION_LEGACY_CUTOFF_MS = Date.parse("2026-04-05T15:30:00Z");

function hasLegacyCompletedOnboarding(account = {}) {
  const createdAtMs = Date.parse(String(account?.createdAt || account?.created_at || ""));
  return Number.isFinite(createdAtMs) && createdAtMs < ONBOARDING_COMPLETION_LEGACY_CUTOFF_MS;
}

export function shouldRedirectSignedInUserToPreferences(accountPayload) {
  const account = accountPayload?.account || {};
  const status = String(accountPayload?.account?.status || "").trim().toUpperCase();
  const onboardingCompletedAt = String(account.onboardingCompletedAt || "").trim();
  return status === "WORKING" && (Boolean(onboardingCompletedAt) || hasLegacyCompletedOnboarding(account));
}

export function shouldResumeLinkedAccountSetup(accountPayload) {
  const account = accountPayload?.account || {};
  const status = String(accountPayload?.account?.status || "").trim().toUpperCase();
  const onboardingCompletedAt = String(account.onboardingCompletedAt || "").trim();
  return status === "WORKING" && !onboardingCompletedAt && !hasLegacyCompletedOnboarding(account);
}

export function resolveRelinkAccountId(accountsPayload, { requestedAccountId = "" } = {}) {
  const accounts = Array.isArray(accountsPayload?.accounts) ? accountsPayload.accounts : [];
  const normalizedRequestedAccountId = String(requestedAccountId || "").trim();
  if (
    normalizedRequestedAccountId
    && accounts.some((account) => String(account?.id || "").trim() === normalizedRequestedAccountId)
  ) {
    return normalizedRequestedAccountId;
  }
  const primaryAccount = accounts.find((account) => account?.isPrimary);
  return String(primaryAccount?.id || accounts[0]?.id || "").trim();
}

export function shouldBypassSuccessAuthForAuthenticatedUser({
  isSignInSurface = false,
  authStatus = "",
  storedUserMode = "",
  handoffToken = "",
  accountPayload = null,
  languageStepComplete = false,
} = {}) {
  const normalizedAuthStatus = String(authStatus || "").trim().toLowerCase();
  const normalizedStoredUserMode = String(storedUserMode || "").trim().toLowerCase();
  const normalizedHandoffToken = String(handoffToken || "").trim();

  return !isSignInSurface
    && normalizedAuthStatus === "idle"
    && normalizedStoredUserMode !== "new"
    && !normalizedHandoffToken
    && languageStepComplete
    && shouldRedirectSignedInUserToPreferences(accountPayload);
}

export function shouldApplySuggestedLinkedHomeLanguage({
  isSignInSurface = false,
  languageTouched = false,
  currentLanguageCode = "",
} = {}) {
  if (isSignInSurface || languageTouched) {
    return false;
  }
  const normalizedCurrent = normalizeLanguageCode(currentLanguageCode, "");
  return !normalizedCurrent || normalizedCurrent === DEFAULT_LANGUAGE_CODE;
}

export function buildSecondaryLanguageOptions(
  languageOptions = [],
  { homeLanguageCode = "" } = {},
) {
  const normalizedHomeLanguageCode = normalizeLanguageCode(homeLanguageCode, "");
  return (Array.isArray(languageOptions) ? languageOptions : []).filter(
    (language) => language?.code && language.code !== normalizedHomeLanguageCode,
  );
}

export function buildLinkedAccountLanguagePayload({
  homeLanguageCode = "",
  locale = DEFAULT_LANGUAGE_CODE,
  understoodLanguageCodes = [],
} = {}) {
  return {
    preferredUiLanguageCode: normalizeUiLanguageCode(homeLanguageCode || locale, locale),
    languagePreferences: {
      home: [
        {
          languageCode: normalizeLanguageCode(homeLanguageCode || locale),
          rank: 0,
        },
      ],
      understands: (Array.isArray(understoodLanguageCodes) ? understoodLanguageCodes : [])
        .filter((languageCode) => languageCode && languageCode !== homeLanguageCode)
        .map((languageCode, index) => ({
          languageCode: normalizeLanguageCode(languageCode),
          rank: index,
        })),
    },
  };
}

export async function persistLinkedAccountLanguages({
  currentAccountId = "",
  handoffToken = "",
  email = "",
  languagePayload = {},
  adoptOnboardingAccount,
  saveAccountLanguages,
  setCurrentAccountId,
  clearOnboardingHandoffToken,
} = {}) {
  const normalizedCurrentAccountId = String(currentAccountId || "").trim();
  const normalizedHandoffToken = String(handoffToken || "").trim();
  const normalizedEmail = String(email || "").trim();

  let resolvedAccountId = normalizedCurrentAccountId;
  let selectedResolvedAccount = false;
  let adoptedWithHandoff = false;
  if (normalizedHandoffToken) {
    const adoptedPayload = await adoptOnboardingAccount(
      normalizedHandoffToken,
      normalizedEmail ? { email: normalizedEmail } : {},
    );
    resolvedAccountId = String(
      adoptedPayload?.account?.id || resolvedAccountId,
    ).trim();
    if (!resolvedAccountId) {
      throw new Error("No linked account is available yet.");
    }
    if (typeof setCurrentAccountId === "function") {
      setCurrentAccountId(resolvedAccountId);
      selectedResolvedAccount = true;
    }
    adoptedWithHandoff = true;
  }

  if (!resolvedAccountId) {
    throw new Error("This sign-in handoff expired. Start again from the share link.");
  }
  if (!selectedResolvedAccount && typeof setCurrentAccountId === "function") {
    setCurrentAccountId(resolvedAccountId);
  }
  try {
    await saveAccountLanguages(resolvedAccountId, languagePayload);
  } catch (error) {
    if (
      adoptedWithHandoff
      && selectedResolvedAccount
      && typeof clearOnboardingHandoffToken === "function"
    ) {
      clearOnboardingHandoffToken();
    }
    throw error;
  }
  if (adoptedWithHandoff && typeof clearOnboardingHandoffToken === "function") {
    clearOnboardingHandoffToken();
  }
  return resolvedAccountId;
}

export function shouldBootstrapQrLinkSession({ showQrFallback = false, linkSessionId = "", qrPayload = null } = {}) {
  return Boolean(showQrFallback && !String(linkSessionId || "").trim() && !qrPayload);
}

export function shouldPrimeLinkRouteSession({
  isRelink = false,
  willRedirectToPreferences = false,
} = {}) {
  return !isRelink && !willRedirectToPreferences;
}

export function shouldPromoteQrLinkToAwaitingLink({
  showQrFallback = false,
  linkState = "",
  sessionStatus = "",
} = {}) {
  const normalizedLinkState = String(linkState || "").trim().toLowerCase();
  const normalizedSessionStatus = String(sessionStatus || "").trim().toLowerCase();

  return Boolean(
    showQrFallback
    && normalizedLinkState === "ready"
    && (normalizedSessionStatus === "awaiting_pairing_code" || normalizedSessionStatus === "awaiting_link")
  );
}

export function shouldFinalizeLinkedSuccess({
  showQrFallback = false,
  linkState = "",
  sessionStatus = "",
} = {}) {
  const normalizedLinkState = String(linkState || "").trim().toLowerCase();
  const normalizedSessionStatus = String(sessionStatus || "").trim().toLowerCase();

  return normalizedSessionStatus === "linked"
    && (normalizedLinkState === "awaiting_link" || (showQrFallback && normalizedLinkState === "ready"));
}

export function resolveLinkingQrImageSrc(qrPayload) {
  const directImage = String(qrPayload?.qrCode || qrPayload?.qrDataUrl || "").trim();
  if (directImage) {
    return directImage;
  }

  const encodedImage = String(qrPayload?.value || qrPayload?.data || "").trim();
  if (!encodedImage) {
    return null;
  }
  if (encodedImage.startsWith("data:")) {
    return encodedImage;
  }

  const mimeType = String(qrPayload?.mimetype || qrPayload?.mimeType || "image/png").trim() || "image/png";
  return `data:${mimeType};base64,${encodedImage}`;
}
